import { useState } from "react";

/**
 * Botao do manual de uso em PDF do perfil, no rodape da sidebar.
 *
 * O manual NAO e' publico: a API o serve do R2 so' para quem esta logado
 * (`/v1/portal/banco/manual`, `/v1/prefeitura/manual`). Por isso nao ha
 * `<a href>` — link nao manda o header Authorization. O clique baixa o PDF
 * pelo SDK, com o token, e abre o blob numa aba nova, onde o navegador mostra o
 * visualizador proprio (com busca e impressao) sem tirar o operador da tela.
 *
 * A aba e' aberta ANTES do download, ainda dentro do clique: aberta depois do
 * `await`, o bloqueador de pop-up do navegador a recusaria, porque ela ja nao
 * conta mais como resposta a um gesto do usuario.
 */
export function BotaoManual({ baixar, rotulo }: { baixar: () => Promise<Blob>; rotulo: string }) {
  const [abrindo, setAbrindo] = useState(false);
  const [erro, setErro] = useState(false);

  const abrir = async () => {
    if (abrindo) return;
    setErro(false);
    setAbrindo(true);
    const aba = window.open("", "_blank");
    try {
      const url = URL.createObjectURL(await baixar());
      if (aba) {
        aba.opener = null;
        aba.location.href = url;
      } else {
        // Pop-up bloqueado mesmo assim: abre na propria aba, que o voltar desfaz.
        window.location.href = url;
      }
      // O visualizador ja carregou o PDF inteiro quando isto dispara; revogar
      // so' devolve a memoria de uma sessao que fica aberta o dia todo.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      aba?.close();
      setErro(true);
    } finally {
      setAbrindo(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={abrir}
        aria-busy={abrindo || undefined}
        title={`${rotulo} (PDF, abre em nova aba)`}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          padding: "10px 12px",
          borderRadius: 10,
          border: "1px solid var(--border)",
          background: "var(--bg-elev-2)",
          color: "var(--text)",
          fontSize: 13,
          fontWeight: 600,
          textAlign: "left",
          cursor: abrindo ? "progress" : "pointer",
          transition: "border-color .15s, color .15s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "var(--accent)";
          e.currentTarget.style.color = "var(--accent)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "var(--border)";
          e.currentTarget.style.color = "var(--text)";
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v16H6.5A2.5 2.5 0 0 0 4 21.5z" />
          <path d="M4 21.5A2.5 2.5 0 0 1 6.5 19H20" />
          <path d="M8 7.5h8M8 11h6" />
        </svg>
        <span style={{ flex: 1 }}>{abrindo ? "Abrindo…" : rotulo}</span>
        <span aria-hidden="true" style={{ fontSize: 12, opacity: 0.6 }}>↗</span>
      </button>
      {erro ? (
        <div role="alert" style={{ marginTop: 8, fontSize: 12, color: "var(--danger-500)" }}>
          Não foi possível abrir o manual. Tente de novo em instantes.
        </div>
      ) : null}
    </>
  );
}
